import i18n from '@/i18n/config';
import { userManager } from '@/config/keycloak';
import { runtimeConfig } from '@/config/runtime';
import type { ErrorResponse } from '@/types/http.types';
import axios, { type AxiosError, type AxiosInstance } from 'axios';

/** Uploads and exports can take a while on the CPU sizing; five minutes is the backend's own budget. */
const REQUEST_TIMEOUT_MS = 300_000;

export const apiClient: AxiosInstance = axios.create({
  baseURL: runtimeConfig.apiUrl,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: REQUEST_TIMEOUT_MS,
});

// Request interceptor: attach the Keycloak access token.
apiClient.interceptors.request.use(
  async (config) => {
    const user = await userManager.getUser().catch(() => null);
    if (user?.access_token) {
      config.headers.Authorization = `Bearer ${user.access_token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

/** True for the shape every backend error carries (`{type, detail, request_id}`). */
export const isApiError = (error: unknown): error is AxiosError<ErrorResponse> =>
  axios.isAxiosError(error) && typeof (error.response?.data as ErrorResponse | undefined)?.detail === 'string';

/** HTTP status of a failed request, or `undefined` for network errors and timeouts. */
export const apiErrorStatus = (error: unknown): number | undefined =>
  axios.isAxiosError(error) ? error.response?.status : undefined;

/**
 * One human-readable message for a failed request.
 *
 * An expired session (401) always reads as "sign in again"; otherwise prefers the backend's
 * `detail` (already a sentence), then names the two failure classes the
 * user can act on (timeout, backend unreachable), and only then falls back to the raw message.
 */
export const apiErrorMessage = (error: unknown, fallback: string): string => {
  // An expired session is the user's to fix by signing in again, whatever the backend's English detail says.
  if (axios.isAxiosError(error) && error.response?.status === 401) return i18n.t('common.signedOut');
  if (isApiError(error) && error.response?.data.detail) {
    return error.response.data.detail;
  }
  if (axios.isAxiosError(error)) {
    // Transport failures never carry a backend message, so the wording is ours — and it has to
    // follow the interface language rather than stay English inside a Czech screen.
    if (error.code === 'ECONNABORTED') return i18n.t('common.timedOut');
    if (error.code === 'ERR_NETWORK') return i18n.t('common.serverUnreachable');
  }
  return fallback;
};

/** Parse the file name out of a `Content-Disposition` header, preferring the RFC 5987 form. */
export const filenameFromDisposition = (header: string | undefined): string | null => {
  if (!header) return null;
  const encoded = /filename\*=UTF-8''([^;]+)/i.exec(header);
  if (encoded) {
    try {
      return decodeURIComponent(encoded[1].trim());
    } catch {
      return encoded[1].trim();
    }
  }
  const plain = /filename="?([^";]+)"?/i.exec(header);
  return plain ? plain[1].trim() : null;
};
